import {Stack, Typography} from '@mui/material'
import {useEffect, useState} from 'react'
import {Link, useHistory} from 'react-router-dom'
import {PATHS, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {useAppDispatch} from 'src/hooks'
import {updateScope} from 'src/slices/pspConfigSlice'
import Button from 'src/components-new/primitives/Button'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import PasswordFieldNew from 'src/components-new/inputs/PasswordFieldNew'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import AuthLayout from 'src/components-new/layout/AuthLayout'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface LoginConfiguration {
  scope: string
  logoPSP?: string
  logoIA?: string
  logoSponsorPsp?: string
  sponsorText?: string
  primaryColor: string
  hasAutoregistration?: boolean
  newStyleEnabled?: boolean
  PSPTitleAndIcon?: {title: string; icon: string}
}

interface LoginPageNewProps {
  config: LoginConfiguration
}

const LoginPageNew = ({config}: LoginPageNewProps) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [submitStatus, setSubmitStatus] = useState<string>()
  const [touched, setTouched] = useState({password: false})

  const isFormValid = identifier.trim().length > 0 && password.length > 0
  const isSubmitting = submitStatus === STATUS.PENDING

  useEffect(() => {
    if (config.scope) dispatch(updateScope(config.scope))
    if (config.PSPTitleAndIcon) {
      if (config.PSPTitleAndIcon.title) document.title = config.PSPTitleAndIcon.title
      const link = document.querySelector('link[rel="shortcut icon"]')
      if (link && config.PSPTitleAndIcon.icon) {
        link.setAttribute('href', `data:image/png;base64,${config.PSPTitleAndIcon.icon}`)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    if (submitStatus === STATUS.FULFILLED) history.push('/')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitStatus])

  const clearError = () => {
    if (submitStatus === STATUS.REJECTED) setSubmitStatus(undefined)
  }

  const handleIdentifierChange = (v: string) => {
    setIdentifier(v)
    clearError()
  }

  const handlePasswordChange = (v: string) => {
    setPassword(v)
    clearError()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({password: true})
    if (!isFormValid) return
    await apiClient.login({identifier, password}, setSubmitStatus)
  }

  const errors = {
    password: touched.password && !password ? 'Campo obbligatorio' : '',
  }

  const logo = config.logoPSP ? (
    <img
      alt="logoPSP"
      src={`data:image/png;base64,${config.logoPSP}`}
      style={{maxHeight: 64, maxWidth: 200, objectFit: 'contain'}}
    />
  ) : undefined

  const footer = (config.logoIA || config.logoSponsorPsp) ? (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      {config.logoIA && (
        <img
          alt="logoIA"
          src={`data:image/png;base64,${config.logoIA}`}
          style={{maxHeight: 22, objectFit: 'contain'}}
        />
      )}
      {config.logoSponsorPsp && (
        <Stack direction="row" alignItems="center" spacing={1}>
          {config.sponsorText && (
            <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500]}}>
              {config.sponsorText}
            </Typography>
          )}
          <img
            alt="logoSponsor"
            src={`data:image/png;base64,${config.logoSponsorPsp}`}
            style={{maxHeight: 18, maxWidth: 100, objectFit: 'contain'}}
          />
        </Stack>
      )}
    </Stack>
  ) : undefined

  return (
    <AuthLayout logo={logo} footer={footer}>
      <Stack component="form" onSubmit={handleSubmit} spacing={2.5}>

        {submitStatus === STATUS.REJECTED && (
          <InlineMessage severity="error">
            {'Nome utente o password errati'}
          </InlineMessage>
        )}

        <TextFieldNew
          label="Username o email"
          name="identifier"
          value={identifier}
          onChange={handleIdentifierChange}
          placeholder="es. mario.rossi oppure mario.rossi@email.com"
          autoComplete="username"
          autoFocus
          fullWidth
          required
          disabled={isSubmitting}
        />

        <PasswordFieldNew
          label="Password"
          name="password"
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => setTouched(t => ({...t, password: true}))}
          placeholder="password"
          autoComplete="current-password"
          fullWidth
          required
          disabled={isSubmitting}
          error={!!errors.password}
          helperText={errors.password}
          showCapsLockWarning
        />

        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={!isFormValid}
          loading={isSubmitting}
          sx={{mt: 0.5}}
        >
          {'Log in'}
        </Button>

        <Stack direction="row" justifyContent="space-between">
          <Link to={PATHS.PASSWORD_RECOVERY} style={{textDecoration: 'none'}}>
            <Typography variant="body2" sx={{color: 'primary.main'}}>
              {'Password dimenticata?'}
            </Typography>
          </Link>
          {config.hasAutoregistration && (
            <Link to={PATHS.AUTO_REGISTRATION} style={{textDecoration: 'none'}}>
              <Typography variant="body2" sx={{color: 'primary.main'}}>
                {'Registrami'}
              </Typography>
            </Link>
          )}
        </Stack>
      </Stack>
    </AuthLayout>
  )
}

export default LoginPageNew
